import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import staticTexts from './translations.json';

const I18nContext = createContext();

const LANGS = { pt: 'PT', en: 'EN' };

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('ibrc-lang') || 'pt');
  const [texts, setTexts] = useState(staticTexts);

  // Background refresh from Supabase (picks up changes without redeploy)
  useEffect(() => {
    supabase.from('site_texts').select('key, text_pt, text_en').then(({ data }) => {
      if (data) {
        const map = {};
        data.forEach((row) => {
          map[row.key] = { pt: row.text_pt, en: row.text_en || row.text_pt };
        });
        setTexts(map);
      }
    });
  }, []);

  // Persist language choice
  useEffect(() => {
    localStorage.setItem('ibrc-lang', lang);
    document.documentElement.lang = lang === 'pt' ? 'pt-BR' : 'en';
  }, [lang]);

  // Get translated text by key
  const t = useCallback((key, fallback) => {
    const entry = texts[key];
    if (!entry) return fallback || key;
    return entry[lang] || entry.pt || fallback || key;
  }, [texts, lang]);

  // Get field from a database row (e.g., row.title_pt / row.title_en)
  const tf = useCallback((row, field) => {
    if (!row) return '';
    const localized = row[`${field}_${lang}`];
    const fallback = row[`${field}_pt`] || row[field];
    return localized || fallback || '';
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === 'pt' ? 'en' : 'pt'));
  }, []);

  return (
    <I18nContext.Provider value={{ lang, setLang, toggleLang, t, tf, loading: false, LANGS }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
